import React, { Fragment, useEffect, useState } from "react";
import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { Tabs, Tab } from "@mui/material";
import { Card, Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../components/Admin/Reusable/Breadcrumb";
import { toast } from "react-toastify";
import { isEmpty } from "lodash";
import { createNewReels, fetchReelsById, updateReels } from "../actions/reels";
import SubmitButtonForm from "../components/Admin/Reusable/Form/SubmitButtonForm";
import FormTitleMain from "../components/Admin/Reusable/FormTitleMain";
import InputField from "../components/Admin/Reusable/InputField";
import languagesItems from "../utils/languages";
import { TabPanel, a11yProps } from "../helper/TabPanel";
import SelectStatus from "../components/Admin/Reusable/Form/SelectStatus";

import { useSelector } from "react-redux";
import { basicFormValidationHandler, validateLocalesFields } from "../utils";
import { fetchVideoFormList } from "../actions/video";
import { roleActions } from "../reducers/role";
import GlobalProgressOverlay from "../components/Admin/Reusable/GlobalProgressOverlay";
import { reelsActions } from "../reducers/reels";
import { fetchCourseFormList, fetchCourseList } from "../actions/course";

const ReelsAdd = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();

  const [activeLocaleTab, setActiveLocaleTab] = useState(0);
  const handleChangeLocaleTab = (e, newValue) => setActiveLocaleTab(newValue);
  const [errors, setErrors] = useState({});
  const { reelsDetail, loading, success, actionType } = useSelector(
    (state) => state.reelsReducer
  );
  const { videoFormList } = useSelector((state) => state.videoReducer);
  const { courseFormList, courseList } = useSelector(
    (state) => state.courseReducer
  );

  //locales
  // const [locales, setLocales] = useState(() =>
  //   Object.fromEntries(
  //     languagesItems.map((l) => [
  //       l.code,
  //       {
  //         id: null,
  //         name: "",
  //         description: "",
  //         language: l.code,
  //       },
  //     ])
  //   )
  // );
  const getInitialLocales = () =>
    Object.fromEntries(
      languagesItems.map((l) => [
        l.code,
        { id: null, name: "", description: "", language: l.code },
      ])
    );

  const [locales, setLocales] = useState(getInitialLocales);
  const initialState = {
    videoLink: "",
    videoId: "",
    courseId: "",
    externalLink: "",
    active: true,
    videoTargetId: "",
  };

  //osnovi podaci
  const [data, setData] = useState(initialState);
  const [audienceType, setAudienceType] = useState("internal");
  const [progress, setProgress] = useState(0);
  const [showLoader, setShowLoader] = useState(false);
  //breadcrumbs
  const translateForm = "reelsForm";
  const translateModule = "reels";
  const pageType = id ? "Edit" : "Add";
  const breadcrumbItems = [
    { label: t(`breadcrumb.${translateModule}`) },
    {
      label: t(`breadcrumb.${translateModule}${pageType}`),
      active: true,
    },
  ];

  useEffect(() => {
    dispatch(fetchVideoFormList());
    dispatch(fetchCourseList());
    if (!id) {
      // Ako `id` ne postoji, očisti stanje za novu kreaciju
      setData(initialState);
      setLocales(getInitialLocales());
    } else {
      // Ako `id` postoji, učitaj podatke za korisnika
      dispatch(fetchReelsById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (reelsDetail && id) {
      setData({
        ...reelsDetail,
      });

      if (reelsDetail?.externalLink) {
        setAudienceType("external");
      } else if (reelsDetail?.videoId) {
        setAudienceType("internal");
      } else {
        setAudienceType("internalCourse");
      }

      // popuni locales iz backend-a
      const filledLocales = Object.fromEntries(
        languagesItems.map((l) => {
          const found = reelsDetail?.locales?.find(
            (loc) =>
              loc.language === l.code || loc.language === l.label.toUpperCase()
          );
          return [
            l.code,
            {
              id: found?.id || null,
              name: found?.name || "",
              description: found?.description || "",
              language: l.code,
            },
          ];
        })
      );

      setLocales(filledLocales);
    }
  }, [reelsDetail, id, dispatch]);

  useEffect(() => {
    let interval;
    let timeout;

    if (loading) {
      setShowLoader(true);
      setProgress(0);

      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 5;
        });
      }, 100);
    } else if (!loading && showLoader) {
      setProgress(100);
      timeout = setTimeout(() => {
        setShowLoader(false);
        setProgress(0);
      }, 2000);
    }

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [loading]);

  const mapSectionType = (value) => {
    if (!value) return "";
    switch (value) {
      case "MICRO":
        return "MIKROKAT";
      case "MINI":
        return "MINIKAT";
      case "MASTER":
        return "MASTERKAT";
      default:
        return value; // fallback ako dođe nešto neočekivano
    }
  };

  useEffect(() => {
    if (success) {
      const timeout = setTimeout(() => {
        navigate("/reels-list");

        if (actionType === "create") {
          toast.success("Uspešno ste kreirali novi reel", { theme: "colored" });
        } else if (actionType === "update") {
          toast.success("Uspešno ste izmenili reel", { theme: "colored" });
        }

        dispatch(reelsActions.clearError());
      }, 2000);

      return () => clearTimeout(timeout);
    } else {
      dispatch(reelsActions.clearError());
    }
  }, [success]);

  const userValidationHandler = () => {
    let errorsObject = {};

    // univerzalno obavezna polja
    errorsObject = {
      ...errorsObject,
      ...basicFormValidationHandler({
        // videoPlayerId: data.videoPlayerId,
        videoTargetId: data.videoTargetId,
      }),
    };

    if (audienceType === "external") {
      errorsObject = {
        ...errorsObject,
        ...basicFormValidationHandler({
          externalLink: data.externalLink,
        }),
      };
    }

    if (audienceType === "internal") {
      errorsObject = {
        ...errorsObject,
        ...basicFormValidationHandler({
          videoId: data.videoId,
        }),
      };
    }

    if (audienceType === "internalCourse") {
      errorsObject = {
        ...errorsObject,
        ...basicFormValidationHandler({
          courseId: data.courseId,
        }),
      };
    }

    const localeErrors = validateLocalesFields(locales, [
      "name",
      "description",
    ]);
    if (Object.keys(localeErrors).length > 0) {
      errorsObject.locales = localeErrors;
    }

    setErrors(errorsObject);
    if (!isEmpty(errorsObject))
      toast.error("Morate popuniti sva obavezna polja!", {
        theme: "colored",
      });
    return !isEmpty(errorsObject);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (userValidationHandler()) {
      return;
    }

    const formData = new FormData();
    // pretvaranje locales objekta u niz za backend
    const localesArray = Object.entries(locales).map(([code, v]) => ({
      ...v,
      // ako postoji id, prosledi; ako ne, backend može generisati
      id: v.id || undefined,
    }));
    // ❌ Provera da li je bar jedno ime prazno
    const hasEmptyName = localesArray.some(
      (loc) => !loc.name || loc.name.trim() === ""
    );

    if (hasEmptyName) {
      toast.error("Morate uneti naziv za sve jezike", {
        theme: "colored",
      });
      return;
    }

    const dataForm = {
      ...data,
      locales: localesArray,
    };

    if (id) {
      dataForm.id = id;
    }

    if (id) {
      dispatch(updateReels(dataForm, navigate, id));
    } else {
      dispatch(createNewReels(dataForm, navigate));
    }
  };

  const updateLocale = (code, field, value) => {
    setLocales((prev) => ({
      ...prev,
      [code]: { ...prev[code], [field]: value },
    }));
  };

  const handleInputChange = (eOrName, maybeValue) => {
    // 1️⃣ MANUAL CALL (autocomplete, custom setter)
    if (typeof eOrName === "string") {
      const name = eOrName;
      const value = maybeValue;

      setData((prev) => {
        let next = { ...prev, [name]: value };

        if (name === "videoId") {
          next.externalLink = "";
          next.courseId = "";
        }

        if (name === "externalLink") {
          next.videoId = "";
          next.courseId = "";
        }

        if (name === "courseId") {
          next.videoId = "";
          next.externalLink = "";
        }

        return next;
      });

      return;
    }

    // 2️⃣ EVENT CALL (TextField, Checkbox, Radio)
    const e = eOrName;
    const { name, type, value, checked } = e.target;

    setData((prev) => {
      let next = { ...prev, [name]: type === "checkbox" ? checked : value };

      if (name === "videoId") {
        next.externalLink = "";
        next.courseId = "";
      }

      if (name === "externalLink") {
        next.videoId = "";
        next.courseId = "";
      }

      if (name === "courseId") {
        next.videoId = "";
        next.externalLink = "";
      }

      return next;
    });
  };

  return (
    <Fragment>
      <Breadcrumb
        breadcrumbItems={breadcrumbItems}
        type={`${translateModule}${pageType}`}
        id={id}
        pageType={pageType}
      />
      {showLoader && <GlobalProgressOverlay progress={progress} />}
      <Container className="reels-container">
        <Row>
          <Col lg={12}>
            <form onSubmit={handleSubmit} noValidate>
              <Row>
                <Col lg={12}>
                  <div className="text-end mb-2">
                    <SubmitButtonForm id={id} />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col lg={9}>
                  <Row>
                    <Col lg={12}>
                      <Card>
                        <Card.Header>
                          <FormTitleMain />
                        </Card.Header>
                        <Card.Body>
                          <Col lg={12} className="mb-2">
                            <FormControl>
                              <RadioGroup
                                row
                                name="audienceType"
                                value={audienceType}
                                onChange={(e) =>
                                  setAudienceType(e.target.value)
                                }
                              >
                                <FormControlLabel
                                  value="internal"
                                  control={<Radio />}
                                  label={t(`${translateForm}.internal`)}
                                />
                                <FormControlLabel
                                  value="internalCourse"
                                  control={<Radio />}
                                  label={t(`${translateForm}.internalCourse`)}
                                />
                                <FormControlLabel
                                  value="external"
                                  control={<Radio />}
                                  label={t(`${translateForm}.external`)}
                                />
                              </RadioGroup>
                            </FormControl>
                          </Col>
                          <div className="blog-holder-admin-left-side-text">
                            <Row>
                              <Col lg={6} md={12} xs={12} className="mb-4">
                                <InputField
                                  label={t(`${translateForm}.videoTargetId`)}
                                  name="videoTargetId"
                                  value={data.videoTargetId}
                                  onChange={handleInputChange}
                                  error={errors}
                                  errorsHandler={setErrors}
                                  fullWidth
                                  size="small"
                                  required
                                />
                              </Col>
                              {/* <Col lg={3} md={12} xs={12} className="mb-4">
                                <InputField
                                  label={t(`${translateForm}.videoPlayerId`)}
                                  name="videoPlayerId"
                                  value={data.videoPlayerId}
                                  onChange={handleInputChange}
                                  error={errors}
                                  errorsHandler={setErrors}
                                  fullWidth
                                  size="small"
                                  required
                                />
                              </Col> */}
                              {audienceType === "external" && (
                                <Col lg={6} md={12} xs={12} className="mb-4">
                                  <InputField
                                    label={t(`${translateForm}.externalLink`)}
                                    name="externalLink"
                                    value={data.externalLink}
                                    onChange={handleInputChange}
                                    error={errors}
                                    errorsHandler={setErrors}
                                    fullWidth
                                    size="small"
                                    required={audienceType === "external"}
                                  />
                                </Col>
                              )}
                              {audienceType === "internalCourse" && (
                                <Col lg={6} md={12} xs={12} className="mb-4">
                                  <Autocomplete
                                    options={courseList || []}
                                    getOptionLabel={(option) =>
                                      option?.channelName +
                                        " | " +
                                        option?.name || ""
                                    }
                                    value={
                                      courseList?.find(
                                        (c) => c.id === data.courseId
                                      ) || null
                                    }
                                    onChange={(e, newValue) => {
                                      handleInputChange(
                                        "courseId",
                                        newValue ? newValue.id : 0
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label={t(`${translateForm}.courseId`)}
                                        size="small"
                                        required={
                                          audienceType === "internalCourse"
                                        }
                                        error={!!errors.courseId}
                                        helperText={errors.courseId}
                                      />
                                    )}
                                  />
                                </Col>
                              )}
                              {audienceType === "internal" && (
                                <Col lg={6} md={12} xs={12} className="mb-4">
                                  <Autocomplete
                                    options={videoFormList || []}
                                    getOptionLabel={(option) => {
                                      if (!option) return "";

                                      const parts = [];

                                      if (option.sectionType) {
                                        parts.push(
                                          mapSectionType(option.sectionType)
                                        );
                                      }

                                      if (option.channelName) {
                                        parts.push(option.channelName);
                                      }

                                      if (option.name) {
                                        parts.push(option.name);
                                      }

                                      return parts.join(" - ");
                                    }}
                                    value={
                                      videoFormList?.find(
                                        (u) => u.id === data.videoId
                                      ) || null
                                    }
                                    onChange={(e, newValue) => {
                                      handleInputChange(
                                        "videoId",
                                        newValue ? newValue.id : 0
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label={t(`${translateForm}.videoId`)}
                                        size="small"
                                        error={!!errors.videoId}
                                        helperText={errors.videoId}
                                        required
                                        multiline
                                      />
                                    )}
                                  />
                                </Col>
                              )}
                            </Row>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col lg={12}>
                      <Card>
                        <Card.Header>
                          <FormTitleMain title="Prevodi" />
                        </Card.Header>
                        <Card.Body>
                          {/* Tabs header */}
                          <Tabs
                            value={activeLocaleTab}
                            onChange={handleChangeLocaleTab}
                            variant="scrollable"
                            // scrollButtons
                            // allowScrollButtonsMobile
                          >
                            {languagesItems.map((lng, idx) => (
                              <Tab
                                key={lng.code}
                                label={lng.label}
                                {...a11yProps(idx)}
                                // primer: pokaži zvezdicu ako fali naslov
                                iconPosition="end"
                              />
                            ))}
                          </Tabs>

                          {/* Tabs content */}
                          {languagesItems.map((lng, idx) => (
                            <TabPanel
                              key={lng.code}
                              value={activeLocaleTab}
                              index={idx}
                            >
                              <Row>
                                <input type="hidden" value={lng.id} name="id" />
                                <Col lg={12} className="mb-4">
                                  <InputField
                                    name={`name_${lng.code}`}
                                    label={`${t(`${translateForm}.name`)} `}
                                    value={locales[lng.code].name}
                                    size="small"
                                    onChange={(e) =>
                                      updateLocale(
                                        lng.code,
                                        "name",
                                        e.target.value
                                      )
                                    }
                                    error={errors?.locales?.[lng.code]?.name}
                                    errorsHandler={setErrors}
                                    required
                                  />
                                </Col>
                                <Col lg={12} className="mb-3">
                                  <InputField
                                    name={`description_${lng.code}`}
                                    label={`${t(
                                      `${translateForm}.description`
                                    )} `}
                                    value={locales[lng.code].description || ""}
                                    onChange={(e) =>
                                      updateLocale(
                                        lng.code,
                                        "description",
                                        e.target.value
                                      )
                                    }
                                    error={
                                      errors?.locales?.[lng.code]?.description
                                    }
                                    errorsHandler={setErrors}
                                    fullWidth
                                    size="small"
                                    required
                                    multiline
                                    minRows={4} // ako tvoj InputField prosleđuje TextField, može i rows={4}
                                  />
                                </Col>
                              </Row>
                            </TabPanel>
                          ))}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Col>
                <Col lg={3}>
                  <Row>
                    <Col lg={12}>
                      <Card>
                        <Card.Header>{t(`global.settings`)}</Card.Header>
                        <Card.Body>
                          <SelectStatus
                            active={true}
                            data={data}
                            handleInputChange={handleInputChange}
                          />
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default ReelsAdd;
